// Photo mode's state (decision 0060: pause, a free camera, the HUD hidden, depth of field, time of day, a pose). The
// mechanism only: the game pauses its sim, pushes its screen, asks its camera for a profile and draws the look; this
// keeps where the free camera's pivot is (moved relative to the view's yaw, kept within `radius` of the anchor, the
// hero, and above the ground the game reports), the orbit's yaw, pitch and distance, the time of day and the pose
// chosen, the depth of field, the lens (a field of view: photo mode zooms the lens, not the arm), and what to put back
// on exit. No three.js: plain numbers. Yaw follows the engine's convention (@rpg/camera: the view looks along
// (sin yaw, cos yaw); a turn to the right lowers the yaw).
import { META_FAULTS } from "./faults";

/** A point (metres). */
export interface PhotoVec {
  x: number;
  y: number;
  z: number;
}

/** The game's numbers for the free camera. */
export interface PhotoOptions {
  /** How far the pivot may go from the anchor (m). */
  radius: number;
  /** The lowest the pivot may go above the ground (m). */
  minHeight: number;
  /** Pivot speed (m/s at full input). */
  speed: number;
  /** The lens's field-of-view range (degrees) and the orbit's pitch range (rad, > 0 above the pivot). */
  fov: readonly [number, number];
  pitch: readonly [number, number];
  /** The depth of field's aperture range (the look's own units). */
  aperture: readonly [number, number];
}

/** What the view was when photo mode opened (put back on exit). */
export interface PhotoView {
  yaw: number;
  pitch: number;
  fov: number;
  /** The sky's hour on screen (the sim's clock's). */
  hours: number;
}

/** Photo mode's state now. */
export interface PhotoState {
  active: boolean;
  anchor: PhotoVec;
  pivot: PhotoVec;
  yaw: number;
  pitch: number;
  fov: number;
  /** The hour shown (null: the sim's). */
  hours: number | null;
  /** The pose chosen (null: none). */
  pose: string | null;
  dof: { on: boolean; aperture: number };
  /** The controls panel hidden (a clean frame). */
  panelHidden: boolean;
  /** The view to restore on exit. */
  saved: PhotoView | null;
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

/** Keep a point within `radius` of `anchor` (horizontally and vertically) and above the ground. */
export function boundPivot(p: PhotoVec, anchor: PhotoVec, radius: number, floor: number): PhotoVec {
  let { x, y, z } = p;
  const dx = x - anchor.x;
  const dy = y - anchor.y;
  const dz = z - anchor.z;
  const d = Math.hypot(dx, dy, dz);
  if (d > radius && d > 0) {
    const k = radius / d;
    x = anchor.x + dx * k;
    y = anchor.y + dy * k;
    z = anchor.z + dz * k;
  }
  return { x, y: Math.max(y, floor), z };
}

/** Photo mode. `ground(x, z)` is the ground's height under a point (default 0). */
export class PhotoMode {
  private s: PhotoState;

  constructor(readonly opts: PhotoOptions, private readonly ground: (x: number, z: number) => number = () => 0) {
    const zero = { x: 0, y: 0, z: 0 };
    this.s = { active: false, anchor: zero, pivot: zero, yaw: 0, pitch: 0, fov: opts.fov[1], hours: null, pose: null, dof: { on: true, aperture: opts.aperture[0] }, panelHidden: false, saved: null };
  }

  /** Open at the anchor (the hero's pivot point) with the view as it is. */
  enter(anchor: PhotoVec, view: PhotoView): void {
    const o = this.opts;
    this.s = {
      active: true,
      anchor: { ...anchor },
      pivot: { ...anchor },
      yaw: view.yaw,
      pitch: clamp(view.pitch, o.pitch[0], o.pitch[1]),
      fov: clamp(view.fov, o.fov[0], o.fov[1]),
      hours: null,
      pose: null,
      dof: { ...this.s.dof },
      panelHidden: false,
      saved: { ...view },
    };
  }

  /** Close: returns the view to put back (null if it wasn't open). */
  exit(): PhotoView | null {
    const saved = this.s.saved;
    this.s = { ...this.s, active: false, hours: null, pose: null, panelHidden: false, saved: null };
    return saved;
  }

  get active(): boolean {
    return this.s.active;
  }

  /** Move the pivot by stick-like input over `dt` seconds: `forward` and `right` relative to the view's yaw, `up`
   *  straight up; each -1..1. */
  move(forward: number, right: number, up: number, dt: number): void {
    if (!this.s.active) return;
    const k = this.opts.speed * Math.max(0, dt);
    // the engine's yaw convention (@rpg/camera's `forwardOf` and `rightOf`): forward (sin, cos), right (−cos, sin)
    const fx = Math.sin(this.s.yaw);
    const fz = Math.cos(this.s.yaw);
    const rx = -Math.cos(this.s.yaw);
    const rz = Math.sin(this.s.yaw);
    this.place({ x: this.s.pivot.x + (fx * forward + rx * right) * k, y: this.s.pivot.y + up * k, z: this.s.pivot.z + (fz * forward + rz * right) * k });
  }

  /** Put the pivot at a point (bounded). */
  place(p: PhotoVec): void {
    if (!this.s.active) return;
    const floor = this.ground(p.x, p.z) + this.opts.minHeight;
    this.s.pivot = META_FAULTS.photoUnbounded ? { ...p } : boundPivot(p, this.s.anchor, this.opts.radius, floor);
  }

  /** Turn the orbit (rad): `dYaw` > 0 turns the view right, `dPitch` > 0 raises the camera. Returns the turn applied
   *  (the pitch clamped), for a rig that takes deltas. */
  orbit(dYaw: number, dPitch: number): { dYaw: number; dPitch: number } {
    if (!this.s.active) return { dYaw: 0, dPitch: 0 };
    const was = this.s.pitch;
    this.s.yaw -= dYaw;
    this.s.pitch = clamp(this.s.pitch + dPitch, this.opts.pitch[0], this.opts.pitch[1]);
    return { dYaw, dPitch: this.s.pitch - was };
  }

  /** Follow the rig's own yaw and pitch (it may have clamped or drifted them). */
  sync(yaw: number, pitch: number): void {
    this.s.yaw = yaw;
    this.s.pitch = clamp(pitch, this.opts.pitch[0], this.opts.pitch[1]);
  }

  /** Zoom the lens (a wheel or a pinch): `factor` > 1 zooms in (a narrower field of view). */
  zoom(factor: number): void {
    if (!this.s.active || !(factor > 0)) return;
    this.s.fov = clamp(this.s.fov / factor, this.opts.fov[0], this.opts.fov[1]);
  }

  /** The hour to show (null: the sim's), wrapped to 0..24. */
  setHours(h: number | null): void {
    this.s.hours = h === null ? null : ((h % 24) + 24) % 24;
  }

  setPose(id: string | null): void {
    this.s.pose = id;
  }

  /** Depth of field on or off, and its aperture (clamped to the range). */
  setDof(d: { on?: boolean; aperture?: number }): void {
    this.s.dof = { on: d.on ?? this.s.dof.on, aperture: d.aperture === undefined ? this.s.dof.aperture : clamp(d.aperture, this.opts.aperture[0], this.opts.aperture[1]) };
  }

  setPanelHidden(on: boolean): void {
    this.s.panelHidden = on;
  }

  /** How far the pivot is from the anchor (m). */
  offset(): number {
    const p = this.s.pivot;
    const a = this.s.anchor;
    return Math.hypot(p.x - a.x, p.y - a.y, p.z - a.z);
  }

  /** A copy of the state. */
  state(): PhotoState {
    return structuredClone(this.s);
  }
}
